import { connectionLogger as logger } from '../utils/logger';
import { apiGet, apiPost, apiPut, apiDelete } from './api';

export const connectionService = {
  // Test MCP connection
  testMcpConnection: async (connectionData) => {
    try {
      logger.debug('Testing MCP connection:', connectionData.name);

      const testData = {
        ...connectionData,
        type: 'MCP'
      };

      // MCP test needs longer timeout — STDIO transports may download packages via npx on first run
      const response = await apiPost('connections/test-mcp', testData, { timeout: 120000 });
      const testResult = response.data || response;
      const isSuccess = testResult.success === true;

      return {
        success: isSuccess,
        message: testResult.message || (isSuccess ? 'MCP connection successful' : 'MCP connection failed'),
        durationMs: testResult.durationMs || 0
      };
    } catch (error) {
      logger.error('Error testing MCP connection:', error);

      let errorMessage = 'Failed to test MCP connection';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Build actionable troubleshooting hints based on the error and transport type
      const hints = [];
      const isTimeout = error.name === 'AbortError' || errorMessage.includes('timed out');
      const isStdio = connectionData.mcpTransportType === 'STDIO';

      if (isTimeout) {
        if (isStdio) {
          const cmd = connectionData.mcpCommand || '';
          const args = connectionData.mcpCommandArgs || '';
          const usesNpx = cmd.includes('npx') || args.includes('npx');
          errorMessage = 'MCP server did not start within 120 seconds.';
          if (usesNpx) {
            hints.push('npx may be downloading the package for the first time. Try running the command in a terminal first to warm the cache.');
            const flatArgs = args.replace(/[\[\]"]/g, '').replace(/,\s*/g, ' ').trim();
            hints.push(`Run: ${cmd} ${flatArgs}`);
          }
          hints.push('Verify the command is installed and in PATH on the server.');
          hints.push('Check that Node.js (v18+) is available if using npx.');
        } else {
          errorMessage = 'MCP server did not respond within 120 seconds.';
          hints.push(`Verify the server URL is correct: ${connectionData.mcpServerUrl || '(not set)'}`);
          hints.push('Check that the MCP server is running and accessible from this machine.');
        }
      } else if (errorMessage.includes('Cannot run program') || errorMessage.includes('No such file')) {
        hints.push(`"${connectionData.mcpCommand || 'The command'}" was not found. Ensure it is installed and in the system PATH.`);
        if (connectionData.mcpCommand === 'npx') {
          hints.push('Install Node.js (v18+) to get npx: https://nodejs.org');
        } else if (connectionData.mcpCommand === 'uvx') {
          hints.push('Install uv to get uvx: https://docs.astral.sh/uv/');
        }
      } else if (errorMessage.includes('ECONNREFUSED') || errorMessage.includes('Connection refused')) {
        errorMessage = `Connection refused at ${connectionData.mcpServerUrl || 'the configured URL'}.`;
        hints.push('Verify the MCP server is running and the URL/port are correct.');
      } else if (errorMessage.includes('exit code') || errorMessage.includes('exited')) {
        hints.push('The MCP server process started but exited unexpectedly.');
        hints.push('Check that required environment variables are set (e.g., connection strings, API tokens).');
        hints.push('Try running the command manually in a terminal to see the full error output.');
      }

      return {
        success: false,
        message: errorMessage,
        hints: hints.length > 0 ? hints : undefined
      };
    }
  },

  // Test AI connection
  testAiConnection: async (connectionData) => {
    try {
      logger.debug('Testing AI connection:', connectionData.aiProvider);

      const testData = {
        ...connectionData,
        type: 'AI'
      };

      const response = await apiPost('connections/test-ai', testData);

      // The backend wraps result in ApiResponse, so actual test result is in response.data
      const testResult = response.data || response;
      const isSuccess = testResult.success === true;

      return {
        success: isSuccess,
        message: testResult.message || (isSuccess ? 'AI connection successful' : 'AI connection failed'),
        details: testResult,
        modelInfo: testResult.modelInfo || null,
        responseTime: testResult.responseTime || 0
      };
    } catch (error) {
      logger.error('Error testing AI connection:', error);

      let errorMessage = 'Failed to test AI connection';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
        details: error.response?.data?.details || null
      };
    }
  },


  // Get all connections
  getConnections: async () => {
    try {
      logger.debug('Fetching connections from backend...');
      const response = await apiGet('connections');
      
      // Handle both array response and {data: [...]} response
      if (Array.isArray(response)) {
        logger.debug(`Retrieved ${response.length} connections`);
        return response;
      }
      
      const connections = response.data || [];
      logger.debug(`Retrieved ${connections.length} connections`);
      return connections;
    } catch (error) {
      logger.error('Error in getConnections:', error);
      // Fallback to empty array
      return [];
    }
  },
  
  // Get a specific connection by ID
  getConnection: async (id) => {
    try {
      logger.debug(`Fetching connection details for id: ${id}`);
      const response = await apiGet(`connections/${id}`);
      
      // Handle different response formats
      if (response && typeof response === 'object') {
        if (response.data) {
          return response.data;
        } else {
          // If response is the connection object directly
          return response;
        }
      }
      return null;
    } catch (error) {
      logger.error(`Error fetching connection ${id}:`, error);
      throw error;
    }
  },
  
  // Create a new connection
  createConnection: async (connectionData) => {
    const response = await apiPost('connections', connectionData);
    return response.data;
  },
  
  // Update an existing connection
  updateConnection: async (id, connectionData) => {
    const response = await apiPut(`connections/${id}`, connectionData);
    return response.data;
  },
  
  // Delete a connection
  deleteConnection: async (id) => {
    try {
      logger.debug('connectionService: Attempting to delete connection with ID:', id);
      const response = await apiDelete(`connections/${id}`);
      logger.debug('connectionService: Delete response:', response);
      return response.success;
    } catch (error) {
      logger.error('connectionService: Delete connection failed:', {
        connectionId: id,
        error: error,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },
  
  // Test a connection (without saving it)
  testConnection: async (connectionData) => {
    try {
      const response = await apiPost('connections/test', connectionData);
      return {
        success: response.success !== false,
        message: response.data?.message || (response.success ? 'Connection successful' : 'Connection failed')
      };
    } catch (error) {
      logger.error('Error testing connection:', error);
      return {
        success: false,
        message: error.message || 'An error occurred while testing the connection'
      };
    }
  },
  
  // Test an API connection and get the response
  testApiConnection: async (connectionData) => {
    // Helper function to test a connection using the standard endpoint
    const testUsingBasicEndpoint = async (data) => {
      try {
        const response = await apiPost('connections/test', data);
        return {
          success: response.success !== false,
          message: response.data?.message || (response.success ? 'Connection successful' : 'Connection failed')
        };
      } catch (err) {
        logger.error('Error in basic connection test:', err);
        return {
          success: false,
          message: err.message || 'Connection test failed'
        };
      }
    };
    
    try {
      logger.debug('Testing API connection:', connectionData.url);
      
      // Prepare minimal request with just the essentials
      const testData = {
        name: connectionData.name || 'Test Connection',
        type: 'API',
        url: connectionData.url,
        method: connectionData.method || 'GET',
        // Only include fields that have values
        ...(connectionData.headers && { headers: connectionData.headers }),
        ...(connectionData.body && { body: connectionData.body })
      };
      
      logger.debug('Sending API test data:', JSON.stringify(testData, null, 2));
      
      // Try the API call with more detailed error handling
      try {
        // Make the actual API call
        const response = await apiPost('connections/test-api', testData);
        
        // If the response indicates an error
        if (!response.success) {
          logger.warn('API test returned failure:', response);
          
          // If endpoint not found, fall back to basic test
          if (response.error?.includes('endpoint not found') || response.error?.includes('404')) {
            logger.warn('API test-api endpoint not found, falling back to regular test endpoint');
            const fallbackResponse = await testUsingBasicEndpoint(testData);
            
            return {
              success: fallbackResponse.success,
              message: fallbackResponse.message || 'API connection test completed',
              error: 'API response endpoint not available - using basic connection test',
              data: fallbackResponse.success ? "Connection successful, but detailed response view is not available." : null,
              statusCode: fallbackResponse.success ? 200 : 0,
              contentType: 'text/plain',
              responseTime: 0,
              responseHeaders: {}
            };
          }
          
          return {
            success: false,
            message: response.message || 'API connection test failed',
            error: response.error || 'Unknown error',
            data: response.data || null,
            statusCode: 0,
            contentType: 'text/plain',
            responseTime: 0,
            responseHeaders: {}
          };
        }
        
        // For successful responses
        return {
          success: true,
          message: response.data?.message || 'API connection successful',
          error: null,
          data: response.data?.data || response.data || null,
          statusCode: response.data?.statusCode || 200,
          contentType: response.data?.contentType || 'application/json',
          responseTime: response.data?.responseTime || 0,
          responseHeaders: response.data?.responseHeaders || {}
        };
    } catch (error) {
        // If the test-api endpoint fails, try the basic endpoint
        logger.warn('Error using test-api endpoint, falling back to basic test:', error);
        const fallbackResponse = await testUsingBasicEndpoint(testData);
        
          return {
            success: fallbackResponse.success,
            message: fallbackResponse.message || 'API connection test completed',
            error: 'API response endpoint not available - using basic connection test',
            data: fallbackResponse.success ? "Connection successful, but detailed response view is not available." : null,
            statusCode: fallbackResponse.success ? 200 : 0,
            contentType: 'text/plain',
            responseTime: 0,
            responseHeaders: {}
          };
      }
    } catch (error) {
      logger.error('Error executing API connection test:', error);
      return {
        success: false,
        message: 'Failed to test API connection',
        error: error.message || 'Unknown error occurred',
        data: null,
        statusCode: 0,
        contentType: 'text/plain',
        responseTime: 0,
        responseHeaders: {}
      };
    }
  },
  
  // Test SFTP connection
  testSftpConnection: async (sftpConfig) => {
    try {
      logger.debug('Testing SFTP connection:', sftpConfig.host);

      const response = await apiPost('connections/test-sftp', sftpConfig);

      return {
        success: response.success !== false,
        message: response.data?.message || (response.success ? 'SFTP connection successful' : 'SFTP connection failed')
      };
    } catch (error) {
      logger.error('Error testing SFTP connection:', error);
      return {
        success: false,
        message: error.message || 'An error occurred while testing the SFTP connection'
      };
    }
  },

  // Test SSH connection
  testSshConnection: async (sshConfig) => {
    try {
      logger.debug('Testing SSH connection:', sshConfig.host);

      const response = await apiPost('connections/test-ssh', sshConfig);

      return {
        success: response.success !== false,
        message: response.data?.message || (response.success ? 'SSH connection successful' : 'SSH connection failed'),
        data: response.data
      };
    } catch (error) {
      logger.error('Error testing SSH connection:', error);
      return {
        success: false,
        message: error.message || 'An error occurred while testing the SSH connection'
      };
    }
  },

  // SFTP file operations
  listSftpDirectory: async (connectionId, path = null, includeHidden = false) => {
    try {
      const response = await apiPost(`connections/${connectionId}/sftp/list?path=${encodeURIComponent(path || '')}&includeHidden=${includeHidden}`);
      return response;
    } catch (error) {
      logger.error('Error listing SFTP directory:', error);
      throw error;
    }
  },

  // Browse SFTP directory (alias for listSftpDirectory with enhanced formatting)
  browseSftpDirectory: async (connectionId, path = null, includeHidden = false) => {
    try {
      const response = await apiPost(`connections/${connectionId}/sftp/list?path=${encodeURIComponent(path || '')}&includeHidden=${includeHidden}`);
      
      // Format the response to match the expected format for file browser
      if (response && response.data && Array.isArray(response.data)) {
        return {
          success: true,
          files: response.data.map(file => ({
            name: file.name,
            type: file.isDirectory ? 'DIRECTORY' : 'FILE',
            size: file.size,
            sizeFormatted: file.sizeFormatted,
            lastModified: file.lastModified,
            permissions: file.permissions
          }))
        };
      }
      
      // If response format is different, try to handle it
      return {
        success: true,
        files: response.files || response.data || []
      };
    } catch (error) {
      logger.error('Error browsing SFTP directory:', error);
      throw error;
    }
  },

  downloadSftpFile: async (connectionId, filePath) => {
    try {
      const response = await fetch(`/api/connections/${connectionId}/sftp/download?filePath=${encodeURIComponent(filePath)}`);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }
      return response.blob();
    } catch (error) {
      logger.error('Error downloading SFTP file:', error);
      throw error;
    }
  },

  previewSftpFile: async (connectionId, filePath, maxLines = 100) => {
    try {
      const response = await apiGet(`connections/${connectionId}/sftp/preview?filePath=${encodeURIComponent(filePath)}&maxLines=${maxLines}`);
      return response;
    } catch (error) {
      logger.error('Error previewing SFTP file:', error);
      throw error;
    }
  },

  createSftpDirectory: async (connectionId, directoryPath) => {
    try {
      const response = await apiPost(`connections/${connectionId}/sftp/mkdir?directoryPath=${encodeURIComponent(directoryPath)}`);
      return response;
    } catch (error) {
      logger.error('Error creating SFTP directory:', error);
      throw error;
    }
  },

  deleteSftpPath: async (connectionId, path, isDirectory = false, recursive = false) => {
    try {
      const response = await apiDelete(`connections/${connectionId}/sftp/delete?path=${encodeURIComponent(path)}&isDirectory=${isDirectory}&recursive=${recursive}`);
      return response;
    } catch (error) {
      logger.error('Error deleting SFTP path:', error);
      throw error;
    }
  },

  getSftpFileInfo: async (connectionId, path) => {
    try {
      const response = await apiGet(`connections/${connectionId}/sftp/info?path=${encodeURIComponent(path)}`);
      return response;
    } catch (error) {
      logger.error('Error getting SFTP file info:', error);
      throw error;
    }
  },

  // Check connection status for an existing connection
  checkConnectionStatus: async (id) => {
    try {
      logger.debug(`Checking connection status for ID: ${id}`);
      const response = await apiGet(`connections/${id}/status`);

      logger.debug('Connection status API response:', response);

      // The response has structure: { success: true, data: ConnectionStatusDTO }
      // We need to check the 'connected' field in the data, not the 'success' field
      const connectionData = response.data;
      return {
        isConnected: connectionData && connectionData.connected,
        message: (connectionData && connectionData.message) || 'Connection status unknown',
        responseTime: connectionData && connectionData.responseTimeMs,
        lastChecked: new Date().toISOString()
      };
    } catch (error) {
      logger.error("Connection status API failed. Error:", error);

      // Throw the error instead of using mock fallback
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to check connection status'
      );
    }
  },

  // Kafka Connect methods
  getKafkaConnectPlugins: async () => {
    try {
      logger.debug('Fetching Kafka Connect plugins...');
      const response = await apiGet('connections/kafka-connect/plugins');
      return {
        success: response.success !== false,
        data: response.data || response,
        message: response.message || 'Successfully retrieved connector plugins'
      };
    } catch (error) {
      logger.error('Error fetching Kafka Connect plugins:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Failed to fetch connector plugins'
      };
    }
  },

  deployKafkaConnector: async (connectionId) => {
    try {
      logger.debug(`Deploying Kafka Connect connector for connection: ${connectionId}`);
      const response = await apiPost(`connections/kafka-connect/deploy/${connectionId}`);
      return {
        success: response.success !== false,
        data: response.data || response,
        message: response.message || 'Connector deployed successfully'
      };
    } catch (error) {
      logger.error('Error deploying Kafka Connect connector:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to deploy connector'
      };
    }
  },

  getKafkaConnectorStatus: async (connectionId) => {
    try {
      logger.debug(`Getting Kafka Connect connector status for connection: ${connectionId}`);
      const response = await apiGet(`connections/kafka-connect/status/${connectionId}`);
      return {
        success: response.success !== false,
        data: response.data || response,
        message: response.message || 'Successfully retrieved connector status'
      };
    } catch (error) {
      logger.error('Error getting Kafka Connect connector status:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to get connector status'
      };
    }
  },

  generateMySQLConnectorConfig: async (mysqlConfig) => {
    try {
      logger.debug('Generating MySQL connector configuration...');
      const response = await apiPost('connections/kafka-connect/generate-mysql-config', mysqlConfig);
      return {
        success: response.success !== false,
        data: response.data || response,
        message: response.message || 'Successfully generated MySQL connector configuration'
      };
    } catch (error) {
      logger.error('Error generating MySQL connector configuration:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to generate configuration'
      };
    }
  },

  // Get table names from a connection
  getTableNames: async (connectionId) => {
    try {
      logger.debug(`Fetching table names for connection: ${connectionId}`);
      const response = await apiGet(`metadata/tables?connectionId=${connectionId}`);
      return {
        success: response.success !== false,
        data: response.data || [],
        message: response.message || 'Successfully retrieved table names'
      };
    } catch (error) {
      logger.error('Error fetching table names:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Failed to fetch table names'
      };
    }
  },

  // Get table columns from a connection
  getTableColumns: async (connectionId, tableName) => {
    try {
      logger.debug(`Fetching columns for table ${tableName} in connection: ${connectionId}`);
      const response = await apiGet(`metadata/columns?connectionId=${connectionId}&table=${tableName}`);
      return {
        success: response.success !== false,
        data: response.data || [],
        message: response.message || 'Successfully retrieved table columns'
      };
    } catch (error) {
      logger.error('Error fetching table columns:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Failed to fetch table columns'
      };
    }
  },

  // Stop (delete) Kafka Connect connector
  stopKafkaConnector: async (connectionId) => {
    try {
      logger.debug(`Stopping Kafka Connect connector for connection: ${connectionId}`);
      const response = await apiDelete(`connections/kafka-connect/stop/${connectionId}`);
      return {
        success: response.success !== false,
        message: response.message || 'Connector stopped successfully'
      };
    } catch (error) {
      logger.error('Error stopping Kafka Connect connector:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to stop connector'
      };
    }
  },

  // Pause Kafka Connect connector
  pauseKafkaConnector: async (connectionId) => {
    try {
      logger.debug(`Pausing Kafka Connect connector for connection: ${connectionId}`);
      const response = await apiPut(`connections/kafka-connect/pause/${connectionId}`, {});
      return {
        success: response.success !== false,
        message: response.message || 'Connector paused successfully'
      };
    } catch (error) {
      logger.error('Error pausing Kafka Connect connector:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to pause connector'
      };
    }
  },

  // Resume Kafka Connect connector
  resumeKafkaConnector: async (connectionId) => {
    try {
      logger.debug(`Resuming Kafka Connect connector for connection: ${connectionId}`);
      const response = await apiPut(`connections/kafka-connect/resume/${connectionId}`, {});
      return {
        success: response.success !== false,
        message: response.message || 'Connector resumed successfully'
      };
    } catch (error) {
      logger.error('Error resuming Kafka Connect connector:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to resume connector'
      };
    }
  },

  // Test Kafka Connect connection
  testKafkaConnectConnection: async (connectionData) => {
    try {
      logger.debug('Testing Kafka Connect connection:', connectionData.name);
      const response = await apiPost('connections/test-kafka-connect', connectionData);
      return {
        success: response.success !== false,
        message: response.message || 'Kafka Connect connection test successful'
      };
    } catch (error) {
      logger.error('Error testing Kafka Connect connection:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to test Kafka Connect connection'
      };
    }
  },

  // Test Web API connection (Webhook, GraphQL, Kafka REST)
  testWebApiConnection: async (connectionData) => {
    try {
      logger.debug('Testing Web API connection:', connectionData.name, connectionData.webApiType);
      
      // Prepare the test data for the backend
      const testData = {
        ...connectionData,
        type: 'WEB_API'
      };
      
      logger.debug('Sending Web API test data:', JSON.stringify(testData, null, 2));
      
      const response = await apiPost('connections/test-web-api', testData);
      
      // Handle successful responses
      if (response.success !== false) {
        return {
          connected: true,
          success: true,
          message: response.message || 'Web API connection test successful',
          info: response.info || [],
          warnings: response.warnings || [],
          errors: response.errors || [],
          responseTime: response.responseTime || 0,
          testedEndpoint: response.testedEndpoint,
          webApiType: response.webApiType
        };
      } else {
        return {
          connected: false,
          success: false,
          message: response.message || 'Web API connection test failed',
          info: response.info || [],
          warnings: response.warnings || [],
          errors: response.errors || [],
          responseTime: response.responseTime || 0,
          testedEndpoint: response.testedEndpoint,
          webApiType: response.webApiType
        };
      }
    } catch (error) {
      logger.error('Error testing Web API connection:', error);
      
      // Handle different error types
      let errorMessage = 'Failed to test Web API connection';
      let errorDetails = [];
      
      if (error.response?.data) {
        errorMessage = error.response.data.message || errorMessage;
        if (error.response.data.errors) {
          errorDetails = error.response.data.errors;
        }
      } else if (error.message) {
        errorMessage = error.message;
        errorDetails.push(error.message);
      }
      
      return {
        connected: false,
        success: false,
        message: errorMessage,
        info: [],
        warnings: [],
        errors: errorDetails,
        responseTime: 0,
        testedEndpoint: null,
        webApiType: connectionData.webApiType
      };
    }
  },
};

export default connectionService;